'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const services = [
  'AppSec & Vulnerability Management',
  'Compliance & Continuous Compliance',
  'Threat Modelling & Architecture',
  'Continuous DevSecOps',
  'IaC & Pipeline Security',
  'Cloud & Platform Engineering',
  'Accelerator Programme',
  'Bespoke Security Programmes',
  'Security Focus Programme',
  'Not sure, need guidance',
];

const inquiryTypes = [
  { value: 'discovery', label: 'Discovery call', desc: 'A 30-minute conversation about your situation' },
  { value: 'proposal', label: 'Proposal request', desc: 'Scoped engagement proposal for a specific need' },
  { value: 'audit', label: 'Audit readiness', desc: 'Preparing for an upcoming audit or assessment' },
  { value: 'incident', label: 'Incident response', desc: 'Urgent security incident or breach response' },
  { value: 'careers', label: 'Careers enquiry', desc: 'Joining the TechSonar team' },
];

const contactDetails = [
  {
    label: 'Email',
    value: 'hello@techsonar.io',
    href: 'mailto:hello@techsonar.io',
  },
  {
    label: 'LinkedIn',
    value: 'linkedin.com/company/techsonar',
    href: 'https://www.linkedin.com/',
  },
];

export default function ContactPage() {
  useScrollAnimation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    inquiryType: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // This used to be `e.preventDefault(); setSubmitted(true);` and sent nothing.
  // Every enquiry through the site's only conversion path was discarded while
  // the visitor was shown a success panel. Same defect found on netru.io and
  // aerialintel.co.uk, both built with the same tool.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/hubspot/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, form: 'contact_page' }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.error || `Could not send that (${res.status}).`);
      }
      // Fire the conversion only once the CRM has accepted the lead. Firing on
      // click reports leads that were never delivered, which is how a broken
      // form stays invisible in analytics.
      if (typeof window !== 'undefined' && typeof (window as unknown as { gtag?: unknown }).gtag === 'function') {
        (window as unknown as { gtag: (...a: unknown[]) => void }).gtag('event', 'generate_lead', {
          form_name: 'contact_page',
        });
      }
      setSubmitted(true);
    } catch (err) {
      setError(
        (err instanceof Error ? err.message : 'Could not send that.') +
          ' Please email hello@techsonar.io and we will pick it up.',
      );
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.25 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.15 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Contact</span>
          </div>
          <div className="max-w-[700px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Get in touch]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Start with a real conversation.
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed fade-in-up" style={{ transitionDelay: '0.15s' }}>
              No sales pitch. No automated follow-up sequence. A 30-minute discovery call with a senior TechSonar engineer to understand your situation and whether we can help.
            </p>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="border-t border-[#222] py-16 lg:py-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-24">

            {/* Form */}
            <div className="lg:col-span-2 fade-in-up">
              {submitted ? (
                <div className="border border-[#80edfa]/30 bg-[#0a0f14] p-12 text-center">
                  <div className="w-12 h-12 border border-[#80edfa]/40 flex items-center justify-center mx-auto mb-6">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10L8 14L16 6" stroke="#80edfa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 className="font-heading text-2xl text-[#f5f5f8] font-medium mb-3">Message received.</h2>
                  <p className="text-[#aaa] text-sm leading-relaxed max-w-[400px] mx-auto">
                    A senior TechSonar engineer will be in touch within one business day to arrange a discovery call.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  {/* Inquiry type */}
                  <div>
                    <label className="block font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-3">
                      What brings you here?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
                      {inquiryTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, inquiryType: type.value }))}
                          className={`bg-[#050a0f] p-4 text-left transition-colors hover:bg-[#0a0f14] ${formData.inquiryType === type.value ? 'bg-[#0a0f14] border-l-2 border-[#80edfa]' : ''}`}
                        >
                          <p className={`text-xs font-sans uppercase tracking-wide mb-1 transition-colors ${formData.inquiryType === type.value ? 'text-[#80edfa]' : 'text-[#aaa]'}`}>{type.label}</p>
                          <p className="text-[0.65rem] text-[#444]">{type.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Service selector */}
                  <div>
                    <label htmlFor="service" className="block font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-3">
                      Area of interest
                    </label>
                    <div className="relative">
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full bg-[#0a0f14] border border-[#222] px-4 py-3 text-sm text-[#f5f5f8] font-sans appearance-none outline-none focus:border-[#80edfa]/50 transition-colors"
                      >
                        <option value="" className="bg-[#0a0f14]">Select a service or programme</option>
                        {services.map((s) => (
                          <option key={s} value={s} className="bg-[#0a0f14]">{s}</option>
                        ))}
                      </select>
                      <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#444] pointer-events-none" viewBox="0 0 16 16" fill="none">
                        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>

                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-3">
                        Name <span className="text-[#80edfa]">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="w-full bg-[#0a0f14] border border-[#222] px-4 py-3 text-sm text-[#f5f5f8] font-sans placeholder-[#333] outline-none focus:border-[#80edfa]/50 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-3">
                        Work email <span className="text-[#80edfa]">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className="w-full bg-[#0a0f14] border border-[#222] px-4 py-3 text-sm text-[#f5f5f8] font-sans placeholder-[#333] outline-none focus:border-[#80edfa]/50 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="company" className="block font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-3">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your organisation"
                      className="w-full bg-[#0a0f14] border border-[#222] px-4 py-3 text-sm text-[#f5f5f8] font-sans placeholder-[#333] outline-none focus:border-[#80edfa]/50 transition-colors"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-3">
                      Tell us about your situation
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What are you working on? What is the timeline? Any upcoming audits or deadlines?"
                      className="w-full bg-[#0a0f14] border border-[#222] px-4 py-3 text-sm text-[#f5f5f8] font-sans placeholder-[#333] outline-none focus:border-[#80edfa]/50 transition-colors resize-none"
                    />
                  </div>

                  <div>
                    {/* The visitor is told when delivery fails. The previous
                        behaviour showed success unconditionally, which is the
                        failure mode being fixed here. */}
                    {error && (
                      <p className="text-sm text-[#ff8a8a] mb-4 leading-relaxed">{error}</p>
                    )}
                    <button type="submit" className="btn-primary" disabled={sending}>
                      {sending ? 'Sending...' : 'Send message'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-8 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              {/* What to expect */}
              <div>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-4">What to expect</p>
                <div className="flex flex-col gap-4">
                  {[
                    { step: '01', text: 'We respond within one business day' },
                    { step: '02', text: '30-minute discovery call with a senior engineer' },
                    { step: '03', text: 'We scope an engagement that fits your situation' },
                    { step: '04', text: 'Work starts within days, not weeks' },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-4">
                      <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest flex-shrink-0 mt-0.5">[{item.step}]</span>
                      <p className="text-sm text-[#666]">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact details */}
              <div className="border-t border-[#222] pt-8">
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-4">Direct contact</p>
                <div className="flex flex-col gap-4">
                  {contactDetails.map((c) => (
                    <div key={c.label}>
                      <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-1">{c.label}</p>
                      <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="text-sm text-[#aaa] hover:text-[#80edfa] transition-colors">
                        {c.value}
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              {/* Urgent */}
              <div className="border border-[#80edfa]/20 p-6">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-2">Urgent incident?</p>
                <p className="text-sm text-[#666] leading-relaxed mb-4">
                  If you are dealing with an active security incident or breach, select &ldquo;Incident response&rdquo; above and we will prioritise your enquiry.
                </p>
                <p className="text-xs text-[#444]">Response within 2 hours during business hours.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
